import { SingleFieldUpdater } from "../utils/useUpdate";
import {
  DISH_TAG_LABELS,
  DishModification,
  DishTag,
  emptyIngredient,
  emptyStep,
  StepIngredientModification,
  StepModification,
} from "../data/dishModification";
import { Draft, produce } from "immer";
import { cloneDeep, values } from "lodash";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { useToast } from "./ui/Toast";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useImmer } from "use-immer";
import { PersonLookup } from "../data/lookups";
import { isDeleted, isEdited, isNew } from "../utils/editUtils";
import { SaveButton } from "./buttons/SaveButton";
import {
  FoodDto,
  createDish,
  createFood,
  deleteAvailability,
  saveAvailabilities,
  updateAvailabilities,
  updateDish,
} from "../api";
import { useUnloadAlert } from "./useUnloadAlert";
import { deepEquals } from "../utils/equalUtils";
import { SearchForFoodDrawer } from "./SearchForFoodDrawer";
import {
  AvailabilityDrawer,
  useAvailabilityDrawer,
} from "./AvailabilityDrawer";
import { usePlan2PlateDisclosure } from "./Plan2PlateDrawer";
import { AiUrlImportDrawer } from "./AiUrlImportDrawer";
import { DishAiSourceSelectionDrawer } from "./DishAiSourceSelectionDrawer";
import { DrawerHistoryContext } from "../contexts";
import { queryKeys } from "../queries/queries";
import {
  useAvailabilitiesByPurchasesAsync,
  useFoodPurchasesAsync,
} from "../queries/caches";
import { useFoodsByIdsAsync } from "../queries/caches";
import { collectFoodUnitIds } from "../utils/foodIdUtils";
import { useQueryClient } from "@tanstack/react-query";
import { seedFood } from "../queries/caches";
import { showValidationMessage } from "./showValidationMessage";
import { formatFoodDescription, getFoodMaybe } from "../utils/foodUtils";
import { getRenderKey } from "../utils/renderUtils";
import { nutriValuesFromFoodUnitWithAmount } from "../utils/planUtils";
import { FoodUnit } from "../data/foodUnit";
import { createPathUpdater } from "../utils/immerUtils";
import { cn } from "../lib/utils";
import { Card } from "./ui/Card";
import { Checkbox } from "./ui/Checkbox";
import { Badge } from "./ui/Badge";
import { Input } from "./ui/Input";
import { Textarea } from "./ui/Textarea";
import { MacroBar } from "./ui/MacroBar";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/Accordion";
import { Glyph, SmallIconButton } from "./SmallIconButton";
import { Counter } from "./ui/Counter";
import { SectionLabel } from "./ui/SectionLabel";
import { Hint } from "./ui/Hint";
import { PersonNutriSummary } from "./ui/PersonNutriCard";
import { AddDashedButton } from "./ui/AddDashedButton";
import { IngredientEditor } from "./dish/IngredientEditor";
import { StepCompactView } from "./dish/StepCompactView";
import {
  addNutri,
  computeDishNutri,
  computeDishWeightG,
  computeStepIngredientNutri,
  computeSuggestedNutriTags,
  DishNutri,
  emptyNutri,
  formatGrams,
  formatKcal,
  scaleNutri,
} from "../utils/dishUtils";

const TAG_OPTIONS = Object.entries(DISH_TAG_LABELS).map(([value, label]) => ({
  value: value as DishTag,
  label,
}));

/** Hamburger-Griff zum Umsortieren der Schritte. */
const DragHandleIcon = () => (
  <svg width={16} height={16} viewBox="0 0 16 16" fill="none" aria-hidden>
    <path
      d="M3 4.5h10M3 8h10M3 11.5h10"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

type SortableStepHandleProps = {
  ref: (node: HTMLElement | null) => void;
  attributes: ReturnType<typeof useSortable>["attributes"];
  listeners: ReturnType<typeof useSortable>["listeners"];
};

/**
 * dnd-kit-Wrapper um einen Schritt. Die Karte bewegt sich beim Ziehen mit dem
 * Finger (Transform am Wrapper), gezogen wird nur am Griff.
 */
function SortableStep(props: {
  id: string | number;
  children: (args: {
    dragHandleProps: SortableStepHandleProps;
    isDragging: boolean;
  }) => React.ReactNode;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props.id });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      className={cn(isDragging && "relative z-20 select-none")}
    >
      {props.children({
        dragHandleProps: {
          ref: setActivatorNodeRef,
          attributes,
          listeners,
        },
        isDragging,
      })}
    </div>
  );
}

export const DishEditor = ({
  isCreate,
  personLookup,
  initialDish,
  afterSave,
  loadAvailabilities,
  variant = "full",
  hideDescription = false,
  hideSaveButton = false,
  onDishChange,
  saveOverride,
}: DishEditorProps) => {
  const isSimple = variant === "simple";
  const queryClient = useQueryClient();
  const scaledInitialDish = useMemo(() => {
    const cloned = cloneDeep(initialDish);
    if (
      cloned.fromCookbook &&
      cloned.recipePortions &&
      cloned.recipePortions > 1
    ) {
      cloned.steps.forEach((step) => {
        step.ingredients.forEach((ing) => {
          ing.amount = ing.amount * cloned.recipePortions!;
          ing.alternatives?.forEach((alt) => {
            if (alt.amount) {
              alt.amount = alt.amount * cloned.recipePortions!;
            }
          });
        });
      });
    }
    return cloned;
  }, [initialDish]);

  const [editedDish, updateEditedDish] =
    useImmer<DishModification>(scaledInitialDish);

  const foodUnitIds = useMemo(
    () => collectFoodUnitIds(editedDish.steps),
    [editedDish.steps],
  );
  const foods = useFoodsByIdsAsync([], foodUnitIds);
  const purchases = useFoodPurchasesAsync(foods.map((food) => food.id));
  const availabilities = useAvailabilitiesByPurchasesAsync(
    purchases.map((purchase) => purchase.id),
  );
  const loadFood = async () => foods;

  useEffect(() => {
    onDishChange?.(editedDish, foods);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editedDish, foods]);

  const { pristine, setPristine } = useUnloadAlert();

  useEffect(() => {
    setPristine(deepEquals(scaledInitialDish, editedDish));
  }, [editedDish, scaledInitialDish]);

  const toast = useToast();

  const availabilityDrawerProps = useAvailabilityDrawer(
    async (availabilities) => {
      const createdAvailabilities = availabilities.filter(isNew);
      const editedAvailabilities = availabilities.filter(isEdited);
      const deletedAvailabilities = availabilities.filter(isDeleted);
      await saveAvailabilities({ body: createdAvailabilities });
      await updateAvailabilities({ body: editedAvailabilities });
      await Promise.all(
        deletedAvailabilities.map(
          async (a) =>
            await deleteAvailability({
              path: { availabilityId: String(a.id) },
            }),
        ),
      );
      await loadAvailabilities();
      return true;
    },
    true,
  );

  const updateEditedDishField: SingleFieldUpdater<DishModification> = (
    k,
    v,
  ) => {
    const draftModifier = (dishDraft: Draft<DishModification>) => {
      dishDraft[k] = v;
    };
    updateEditedDish(draftModifier);
    return produce(editedDish, draftModifier);
  };

  const foodsWithNonDefaultPurchase = useMemo(() => {
    const set = new Set<number>();
    purchases.forEach((p) => {
      if (!p.isDefault) set.add(p.foodId);
    });
    return set;
  }, [purchases]);

  const saveDish = async () => {
    if (!editedDish.description || !editedDish.steps.length) {
      showValidationMessage(toast, "Unvollständiges Gericht");
      return false;
    }

    if (saveOverride) {
      return saveOverride(editedDish);
    }

    if (isCreate && !editedDish.purchaseRelevant) {
      let totalWeight = 0;
      for (const step of editedDish.steps) {
        for (const ing of step.ingredients) {
          const food = getFoodMaybe(ing.foodUnitId, foods);
          const fu = food?.foodUnits.find((u) => u.id === ing.foodUnitId);
          totalWeight += ing.amount * (fu?.netWeight || 0);
        }
      }

      const factor = totalWeight > 0 ? totalWeight : 1;
      const foodUnits: FoodUnit[] = [];
      const portions = editedDish.recipePortions || 1;
      const portionWeight = totalWeight / portions;

      if (portions > 1 && portionWeight > 0 && portionWeight !== totalWeight) {
        foodUnits.push({
          id: -1,
          description: "Portion",
          netWeight: portionWeight,
          grossWeight: portionWeight,
          isDefault: false,
          isAutoCreate: false,
        });
      }

      foodUnits.push({
        id: -1,
        description: "Gericht",
        netWeight: totalWeight,
        grossWeight: totalWeight,
        isDefault: true,
        isAutoCreate: false,
      });

      const foodForSave: any = {
        id: -1,
        description: editedDish.description,
        vendor: editedDish.vendor,
        purchaseRelevant: false,
        baseUnit: "G",
        fat: factor > 0 ? (dishNutri.fat * 100) / factor : 0,
        carbs: factor > 0 ? (dishNutri.carbs * 100) / factor : 0,
        protein: factor > 0 ? (dishNutri.protein * 100) / factor : 0,
        kcal: factor > 0 ? (dishNutri.kcal * 100) / factor : 0,
        foodUnits,
        availabilities: [],
        defaultPurchaseId: -1,
        canBeDeleted: true,
      };

      const axiosResponse = await createFood({ body: foodForSave });
      seedFood(queryClient, axiosResponse.data);

      if (afterSave) {
        afterSave(axiosResponse.data, true);
      }
      return true;
    }

    const portions = editedDish.recipePortions || 1;
    const dishForSave = cloneDeep(editedDish);

    if (dishForSave.fromCookbook && portions > 1) {
      dishForSave.steps.forEach((step) => {
        step.ingredients.forEach((ing) => {
          const originalAmount = ing.amount;
          const amountPerPortion = originalAmount / portions;

          // 1. Scale main ingredient amount to 1 portion
          ing.amount = amountPerPortion;

          // Scale alternatives as well
          ing.alternatives?.forEach((alt) => {
            if (alt.amount) {
              alt.amount = alt.amount / portions;
            }
          });

          // 2. Ensure personchoices are created/updated for standard portions
          if (dishForSave.personDefaultPortions) {
            dishForSave.personDefaultPortions.forEach((p) => {
              const personPortions = p.portions || 1;
              if (personPortions > 1) {
                if (!ing.personChoices) {
                  ing.personChoices = [];
                }
                const existingChoice = ing.personChoices.find(
                  (pc) => pc.personId === p.personId,
                );
                const targetAmount = amountPerPortion * personPortions;
                if (existingChoice) {
                  existingChoice.amount = targetAmount;
                } else {
                  ing.personChoices.push({
                    personId: p.personId,
                    foodUnitId: ing.foodUnitId!,
                    amount: targetAmount,
                  });
                }
              } else {
                if (ing.personChoices) {
                  const existingChoice = ing.personChoices.find(
                    (pc) => pc.personId === p.personId,
                  );
                  if (existingChoice) {
                    existingChoice.amount = amountPerPortion;
                  }
                }
              }
            });
          }
        });
      });
    }

    const axiosResponse = isCreate
      ? await createDish({ body: dishForSave })
      : await updateDish({ body: dishForSave });

    const savedCloned = cloneDeep(axiosResponse.data as DishModification);
    if (
      savedCloned.fromCookbook &&
      savedCloned.recipePortions &&
      savedCloned.recipePortions > 1
    ) {
      savedCloned.steps.forEach((step) => {
        step.ingredients.forEach((ing) => {
          ing.amount = ing.amount * savedCloned.recipePortions!;
          ing.alternatives?.forEach((alt) => {
            if (alt.amount) {
              alt.amount = alt.amount * savedCloned.recipePortions!;
            }
          });
        });
      });
    }

    updateEditedDish(savedCloned);
    afterSave && afterSave(savedCloned, false);
    return true;
  };

  const drawerHistoryContext = useContext(DrawerHistoryContext);
  const {
    isOpen: isOpenAddIngredient,
    onOpen: onOpenAddIngredient,
    onClose: onCloseAddIngredient,
  } = usePlan2PlateDisclosure(drawerHistoryContext);
  const {
    isOpen: isOpenAiSource,
    onOpen: onOpenAiSource,
    onClose: onCloseAiSource,
  } = usePlan2PlateDisclosure(drawerHistoryContext);
  const {
    isOpen: isOpenAiUrl,
    onOpen: onOpenAiUrl,
    onClose: onCloseAiUrl,
  } = usePlan2PlateDisclosure(drawerHistoryContext);
  const [aiImportSource, setAiImportSource] = useState<
    "url" | "text" | "image"
  >("url");
  const [aiImportImage, setAiImportImage] = useState<File | undefined>(
    undefined,
  );
  const [addIngredientStepIndex, setAddIngredientStepIndex] = useState<
    number | undefined
  >(undefined);

  const [activeStep, setActiveStep] = useState<number | undefined>(() =>
    initialDish.steps.length ? initialDish.steps.length - 1 : undefined,
  );
  const accordionValue = activeStep != undefined ? [`step-${activeStep}`] : [];

  const dishNutri = useMemo(
    () => computeDishNutri(editedDish.steps, foods),
    [editedDish.steps, foods],
  );
  const dishWeightG = useMemo(
    () => computeDishWeightG(editedDish.steps, foods),
    [editedDish.steps, foods],
  );
  const suggestedNutriTags = useMemo(
    () => computeSuggestedNutriTags(dishNutri, dishWeightG),
    [dishNutri, dishWeightG],
  );
  const unsetSuggestedTags = suggestedNutriTags.filter(
    (t) => !editedDish.tags?.includes(t),
  );
  const portions = editedDish.recipePortions ?? 1;
  const perPortion =
    portions > 0 ? scaleNutri(dishNutri, 1 / portions) : emptyNutri;

  const activePersons = values(personLookup).filter((p) => p.active);
  const getPersonPortions = (personId: number) =>
    editedDish.personDefaultPortions?.find((p) => p.personId === personId)
      ?.portions ?? 1;
  const setPersonPortions = (personId: number, portions: number) => {
    updateEditedDish((d) => {
      if (!d.personDefaultPortions) d.personDefaultPortions = [];
      const i = d.personDefaultPortions.findIndex(
        (p) => p.personId === personId,
      );
      if (i >= 0) d.personDefaultPortions[i].portions = portions;
      else d.personDefaultPortions.push({ personId, portions });
    });
  };

  const toggleTag = (tag: DishTag) => {
    updateEditedDish((d) => {
      if (!d.tags) d.tags = [];
      const i = d.tags.indexOf(tag);
      if (i >= 0) d.tags.splice(i, 1);
      else d.tags.push(tag);
    });
  };

  const stepHasNonDefaultPurchaseIngredient = (step: StepModification) =>
    step.ingredients.some((ing) => {
      const food = getFoodMaybe(ing.foodUnitId, foods);
      return food != null && foodsWithNonDefaultPurchase.has(food.id);
    });

  // Ingredient breakdown – same food aggregated across all steps
  const ingredientBreakdown = useMemo(() => {
    const byFood = new Map<number, { label: string; nutri: DishNutri }>();
    for (const step of editedDish.steps) {
      for (const ing of step.ingredients) {
        const food = getFoodMaybe(ing.foodUnitId, foods);
        if (!food) continue;
        const nutri = computeStepIngredientNutri(ing, foods);
        const existing = byFood.get(food.id);
        if (existing) {
          existing.nutri = addNutri(existing.nutri, nutri);
        } else {
          byFood.set(food.id, { label: formatFoodDescription(food), nutri });
        }
      }
    }
    return Array.from(byFood.values()).sort(
      (a, b) => b.nutri.kcal - a.nutri.kcal,
    );
  }, [editedDish.steps, foods]);

  const computePersonIngredientBreakdown = (personId: number) => {
    if (editedDish.fromCookbook) {
      const personPortions = getPersonPortions(personId);
      const portionFactor = portions > 0 ? personPortions / portions : 0;
      return ingredientBreakdown
        .map((row) => ({
          label: row.label,
          nutri: scaleNutri(row.nutri, portionFactor),
        }))
        .filter((row) => row.nutri.kcal > 0);
    } else {
      const byFood = new Map<number, { label: string; nutri: DishNutri }>();
      for (const step of editedDish.steps) {
        for (const ing of step.ingredients) {
          const pc = ing.personChoices.find((c) => c.personId === personId);
          if (pc) {
            const food = getFoodMaybe(pc.foodUnitId, foods);
            if (!food) continue;
            const nv = nutriValuesFromFoodUnitWithAmount(
              foods,
              pc.foodUnitId,
              pc.amount,
            );
            const fat = nv.fat ?? 0;
            const carbs = nv.carbs ?? 0;
            const protein = nv.protein ?? 0;
            const nutri = {
              fat,
              carbs,
              protein,
              kcal: Math.max(nv.kcal ?? 0, fat * 9 + carbs * 4 + protein * 4),
            };
            const existing = byFood.get(food.id);
            if (existing) {
              existing.nutri = addNutri(existing.nutri, nutri);
            } else {
              byFood.set(food.id, {
                label: formatFoodDescription(food),
                nutri,
              });
            }
          } else {
            const food = getFoodMaybe(ing.foodUnitId, foods);
            if (!food) continue;
            const nutri = computeStepIngredientNutri(ing, foods);
            const existing = byFood.get(food.id);
            if (existing) {
              existing.nutri = addNutri(existing.nutri, nutri);
            } else {
              byFood.set(food.id, {
                label: formatFoodDescription(food),
                nutri,
              });
            }
          }
        }
      }
      return Array.from(byFood.values())
        .sort((a, b) => b.nutri.kcal - a.nutri.kcal)
        .filter((row) => row.nutri.kcal > 0);
    }
  };

  const computePersonNutri = (personId: number) => {
    if (editedDish.fromCookbook) {
      const personPortions = getPersonPortions(personId);
      const portionFactor = portions > 0 ? personPortions / portions : 0;
      return scaleNutri(dishNutri, portionFactor);
    } else {
      let nutri = emptyNutri;
      for (const step of editedDish.steps) {
        for (const ing of step.ingredients) {
          const pc = ing.personChoices.find((c) => c.personId === personId);
          if (pc) {
            const food = foods.find((f) =>
              f.foodUnits.some((fu) => fu.id === pc.foodUnitId),
            );
            if (food) {
              const nv = nutriValuesFromFoodUnitWithAmount(
                foods,
                pc.foodUnitId,
                pc.amount,
              );
              const fat = nv.fat ?? 0;
              const carbs = nv.carbs ?? 0;
              const protein = nv.protein ?? 0;
              nutri = addNutri(nutri, {
                fat,
                carbs,
                protein,
                kcal: Math.max(nv.kcal ?? 0, fat * 9 + carbs * 4 + protein * 4),
              });
            }
          } else {
            nutri = addNutri(nutri, computeStepIngredientNutri(ing, foods));
          }
        }
      }
      return nutri;
    }
  };

  const addStep = () => {
    updateEditedDish((d) => {
      d.steps.push(emptyStep(d.steps.length));
    });
    setActiveStep(editedDish.steps.length);
  };

  // Ensure at least one step exists for the simple (non-purchaseRelevant) mode
  const ensureSimpleStep = () => {
    if (editedDish.steps.length === 0) {
      updateEditedDish((d) => {
        d.steps.push(emptyStep(0));
      });
    }
  };

  // ── Schritte per Drag-and-drop umsortieren (dnd-kit, Maus + Touch) ─────────
  const stepIds = editedDish.steps.map((s, i) => getRenderKey(s, i));
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = stepIds.indexOf(active.id);
    const to = stepIds.indexOf(over.id);
    if (from < 0 || to < 0) return;
    updateEditedDish((d) => {
      d.steps = arrayMove(d.steps, from, to);
      d.steps.forEach((s, i) => {
        s.position = i;
      });
    });
    // Der aufgeklappte Schritt bleibt derselbe – Index mitverschieben.
    setActiveStep((current) => {
      if (current == null) return current;
      if (current === from) return to;
      if (from < current && to >= current) return current - 1;
      if (from > current && to <= current) return current + 1;
      return current;
    });
  };

  const simpleStep = editedDish.steps[0];
  const simpleStepIngredients = simpleStep?.ingredients ?? [];

  const openAddIngredient = (stepIndex: number) => {
    setAddIngredientStepIndex(stepIndex);
    onOpenAddIngredient();
  };

  let suffix = editedDish.fromCookbook ? " (Rezept Gesamt)" : "";
  return (
    <>
      <AvailabilityDrawer {...availabilityDrawerProps} />
      <DishAiSourceSelectionDrawer
        open={isOpenAiSource}
        onClose={onCloseAiSource}
        onSelectText={() => {
          setAiImportSource("text");
          setAiImportImage(undefined);
          onOpenAiUrl();
        }}
        onSelectUrl={() => {
          setAiImportSource("url");
          setAiImportImage(undefined);
          onOpenAiUrl();
        }}
        onSelectImage={(file) => {
          setAiImportSource("image");
          setAiImportImage(file);
          onOpenAiUrl();
        }}
      />
      <AiUrlImportDrawer
        mode="dishImport"
        source={aiImportSource}
        imageFile={aiImportImage}
        open={isOpenAiUrl}
        onClose={onCloseAiUrl}
        onImported={(dish) => {
          updateEditedDish((d) => {
            d.description = dish.description;
            d.purchaseRelevant = true;
            d.fromCookbook = true;
            d.vendor = dish.vendor ?? "";
            d.steps = dish.steps;
            d.tags = dish.tags ?? [];
            d.recipePortions = dish.recipePortions ?? 1;
            if (!d.personDefaultPortions) d.personDefaultPortions = [];
          });
          onCloseAiUrl();
          onCloseAiSource();
        }}
      />
      <SearchForFoodDrawer
        open={isOpenAddIngredient}
        onClose={onCloseAddIngredient}
        selectFood={(food) => {
          if (addIngredientStepIndex == undefined) return;
          updateEditedDish((d) => {
            if (!d.steps[addIngredientStepIndex!]) {
              d.steps[addIngredientStepIndex!] = emptyStep(
                addIngredientStepIndex!,
              );
            }
            d.steps[addIngredientStepIndex!].ingredients.push(
              emptyIngredient(food),
            );
          });
        }}
      />

      <div className="mx-auto flex w-full flex-col gap-2">
        {/* Bezeichnung + Kochbuch + Einkaufsliste */}
        {isSimple ? (
          !hideDescription && (
            <Card className="p-3">
              <Input
                data-cy="dish-description-input"
                value={editedDish.description}
                onChange={(e) =>
                  updateEditedDishField("description", e.target.value)
                }
                placeholder="Bezeichnung"
              />
            </Card>
          )
        ) : (
          <Card className="p-3">
            <div className="flex w-full items-center gap-2">
              <Input
                data-cy="dish-description-input"
                value={editedDish.description}
                onChange={(e) =>
                  updateEditedDishField("description", e.target.value)
                }
                placeholder="Bezeichnung"
                noWFull
                className="flex-1"
              />
              {isCreate && (
                <SmallIconButton
                  label="KI-Modus: Schritt generieren"
                  icon="magicWandSparkles"
                  onClick={onOpenAiSource}
                />
              )}
            </div>
            <div className="mt-2 flex flex-col gap-2">
              {isCreate && editedDish.purchaseRelevant && (
                <label className="flex cursor-pointer items-center gap-3 rounded-card border border-line bg-surface p-2.5">
                  <Checkbox
                    data-cy="dish-from-cookbook-checkbox"
                    checked={!!editedDish.fromCookbook}
                    onCheckedChange={(c) =>
                      updateEditedDishField("fromCookbook", c)
                    }
                  />
                  <span className="text-sm font-medium text-ink">
                    Aus Kochbuch
                  </span>
                </label>
              )}
              <label className="flex cursor-pointer items-center gap-3 rounded-card border border-line bg-surface p-2.5">
                <Checkbox
                  data-cy="dish-purchase-relevant-checkbox"
                  checked={!!editedDish.purchaseRelevant}
                  onCheckedChange={(c) => {
                    updateEditedDish((d) => {
                      d.purchaseRelevant = !!c;
                      if (!c) {
                        d.fromCookbook = false;
                      }
                    });
                  }}
                />
                <span className="text-sm font-medium text-ink">
                  Einkaufsliste-Relevant
                </span>
              </label>
              {!editedDish.purchaseRelevant && (
                <Input
                  value={editedDish.vendor}
                  onChange={(e) =>
                    updateEditedDishField("vendor", e.target.value)
                  }
                  placeholder="Lokal"
                />
              )}

              <Hint className="mt-2 rounded-card border border-line bg-surface p-3 text-xs text-ink-muted">
                {editedDish.purchaseRelevant && editedDish.fromCookbook && (
                  <p>
                    Du kochst dieses Gericht selbst und nimmst das Rezept aus
                    einem Kochbuch. Die Zutaten landen auf der Einkaufsliste,
                    wenn das Gericht geplannt wird.
                  </p>
                )}
                {editedDish.purchaseRelevant && !editedDish.fromCookbook && (
                  <p>
                    Du kochst dieses Gericht selbst und gibst genaue Mengen pro
                    Person an. Die Zutaten landen auf der Einkaufsliste, wenn
                    das Gericht geplannt wird.
                  </p>
                )}
                {!editedDish.purchaseRelevant && (
                  <p>
                    Das Gericht wird als zusammenhängendes Lebensmittel im
                    System gespeichert (mit den Einheiten "Portion" und
                    "Gericht"). Die Gesamtnährwerte und Gewichte werden aus den
                    Zutaten berechnet. Es werden keine Einkäufe generiert. Ideal
                    für Restaurantbesuche oder Lieferdienste.
                  </p>
                )}
              </Hint>
            </div>
          </Card>
        )}

        {/* Portionen + Standardportionen – nur bei "Aus Kochbuch" oder nicht einkaufslisten-relevant */}
        {!isSimple &&
          (!editedDish.purchaseRelevant || editedDish.fromCookbook) && (
            <Card className="p-3">
              <div className="flex items-center justify-between gap-2">
                <SectionLabel>Portionen{suffix}</SectionLabel>
                <Counter
                  dataCy="dish-recipe-portions"
                  value={portions}
                  onChange={(v) => updateEditedDishField("recipePortions", v)}
                  min={1}
                  max={24}
                />
              </div>
              {activePersons.length > 0 && editedDish.purchaseRelevant && (
                <>
                  <div className="my-2 border-t border-line" />
                  <SectionLabel>Standard-Portionen pro Person</SectionLabel>
                  <div
                    className={cn(
                      "grid gap-2",
                      activePersons.length === 1
                        ? "grid-cols-1"
                        : "grid-cols-2",
                    )}
                  >
                    {activePersons.map((person) => {
                      const personPortions = getPersonPortions(person.id);
                      const portionFactor =
                        portions > 0 ? personPortions / portions : 0;
                      const personNutri = scaleNutri(dishNutri, portionFactor);
                      return (
                        <div
                          key={person.id}
                          className="rounded-card border border-line bg-surface p-2"
                        >
                          <div className="text-sm font-semibold text-ink mb-1">
                            {person.name}
                          </div>
                          <div className={"w-full flex justify-center"}>
                            <Counter
                              dataCy={`dish-person-portions-${person.id}`}
                              value={personPortions}
                              onChange={(v) => setPersonPortions(person.id, v)}
                              min={0}
                              max={10}
                            />
                          </div>
                          <div className="mt-1 text-xs text-ink-muted">
                            {Math.round(personNutri.kcal)} kcal
                          </div>
                          <MacroBar
                            className="mt-1"
                            fat={personNutri.fat}
                            carbs={personNutri.carbs}
                            protein={personNutri.protein}
                            kcal={personNutri.kcal}
                            showLabels={false}
                          />
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </Card>
          )}

        {/* Tags */}
        <Card className="p-3">
          <SectionLabel>Tags</SectionLabel>
          <div className="flex flex-wrap gap-1.5">
            {TAG_OPTIONS.map((tag) => {
              const active = editedDish.tags?.includes(tag.value);
              return (
                <Badge
                  key={tag.value}
                  variant={active ? "active" : "muted"}
                  className="cursor-pointer"
                  onClick={() => toggleTag(tag.value)}
                >
                  {tag.label}
                </Badge>
              );
            })}
          </div>
          {unsetSuggestedTags.length > 0 && (
            <Hint className="mt-2 flex items-center gap-2 rounded-card border border-line bg-surface p-2">
              <Glyph tone="gold" icon="warning" size={30} />
              <span className={"text-xs"}>
                <span className="font-semibold text-gold">
                  {unsetSuggestedTags.map((t) => DISH_TAG_LABELS[t]).join(", ")}
                </span>
                {" – das Gericht erfüllt diese Kriterien. Bitte Tags setzen."}
              </span>
            </Hint>
          )}
        </Card>

        {/* Einkaufsliste-Relevant = false → einfache Zutaten-Karte */}
        {(!editedDish.purchaseRelevant || isSimple) && (
          <>
            <Card className="p-3">
              <SectionLabel>Zutaten</SectionLabel>
              <div className="flex flex-col gap-2">
                {simpleStepIngredients.map((ingredient, ingredientIndex) => {
                  const ingredientUpdater =
                    createPathUpdater<StepIngredientModification>(
                      updateEditedDish,
                      `steps[0].ingredients[${ingredientIndex}]`,
                    );
                  const ingFood = getFoodMaybe(ingredient.foodUnitId, foods);
                  return (
                    <IngredientEditor
                      key={getRenderKey(ingredient, ingredientIndex)}
                      ingredient={ingredient}
                      updateIngredient={ingredientUpdater}
                      foods={foods}
                      purchases={purchases}
                      personLookup={personLookup}
                      loadFood={loadFood}
                      fromCookbook={!!editedDish.fromCookbook}
                      purchaseRelevant={!!editedDish.purchaseRelevant}
                      hasNonDefaultPurchase={
                        ingFood != null &&
                        foodsWithNonDefaultPurchase.has(ingFood.id)
                      }
                      editAvailabilities={(purchaseId) =>
                        availabilityDrawerProps.editAvailabilities(
                          purchaseId,
                          availabilities.filter(
                            (a) => a.purchaseId === purchaseId,
                          ),
                        )
                      }
                      deleteIngredient={() =>
                        updateEditedDish((d) => {
                          d.steps[0]?.ingredients.splice(ingredientIndex, 1);
                        })
                      }
                    />
                  );
                })}
              </div>
              <AddDashedButton
                className="mt-2"
                onClick={() => {
                  ensureSimpleStep();
                  openAddIngredient(0);
                }}
              >
                <Glyph icon="plus" size={15} />
                <span>Zutat hinzufügen</span>
              </AddDashedButton>
            </Card>
          </>
        )}

        {/* Einkaufsliste-Relevant = true → volle Zubereitung */}
        {!isSimple && editedDish.purchaseRelevant && (
          <Card className="p-3">
            <SectionLabel className="mb-2">Zubereitung</SectionLabel>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis]}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={stepIds}
                strategy={verticalListSortingStrategy}
              >
                <Accordion
                  value={accordionValue}
                  onValueChange={(items) => {
                    const last = items[items.length - 1];
                    if (!last) setActiveStep(undefined);
                    else {
                      const idx = Number(last.replace("step-", ""));
                      setActiveStep(idx);
                    }
                  }}
                >
                  {editedDish.steps.map((step, stepIndex) => {
                    if (!step) return null;
                    const updateStep = createPathUpdater<StepModification>(
                      updateEditedDish,
                      `steps[${stepIndex}]`,
                    );
                    const isActive = activeStep === stepIndex;
                    const hasNonDefaultPurchaseInStep =
                      stepHasNonDefaultPurchaseIngredient(step);
                    const stepId = getRenderKey(step, stepIndex);
                    return (
                      <SortableStep key={stepId} id={stepId}>
                        {({ dragHandleProps, isDragging }) => (
                          <AccordionItem
                            value={`step-${stepIndex}`}
                            className={cn(
                              "rounded-card border bg-surface-card shadow-card transition",
                              isActive
                                ? "border-gold bg-gold/5"
                                : "border-line",
                              isDragging && "border-teal opacity-70",
                            )}
                          >
                            <div className="flex items-start gap-2 p-2">
                              <div className="flex shrink-0 flex-col items-center gap-1">
                                <AccordionTrigger className="h-6 w-6 justify-center rounded-full bg-surface p-0 text-sm font-bold text-ink">
                                  {stepIndex + 1}
                                </AccordionTrigger>
                                <button
                                  type="button"
                                  ref={dragHandleProps.ref}
                                  {...dragHandleProps.attributes}
                                  {...dragHandleProps.listeners}
                                  data-cy={`dish-step-drag-handle-${stepIndex}`}
                                  aria-label={`Schritt ${stepIndex + 1} verschieben`}
                                  className="flex h-6 w-6 cursor-grab touch-none items-center justify-center rounded-control text-ink-muted hover:bg-surface active:cursor-grabbing"
                                >
                                  <DragHandleIcon />
                                </button>
                              </div>
                              <AccordionTrigger className="w-auto min-w-0 flex-1 items-start p-0">
                                <StepCompactView
                                  step={step}
                                  index={stepIndex}
                                  foods={foods}
                                />
                              </AccordionTrigger>
                            </div>
                            <AccordionContent className="px-2 pb-2 flex flex-col gap-2">
                              <Textarea
                                data-cy={`dish-step-text-input-${stepIndex}`}
                                value={step.text}
                                onChange={(e) =>
                                  updateStep((s) => {
                                    s.text = e.target.value;
                                  })
                                }
                                placeholder="Schritt beschreiben…"
                                rows={3}
                              />
                              <SectionLabel>
                                Zutaten in diesem Schritt
                              </SectionLabel>
                              <div className="flex flex-col gap-2">
                                {step.ingredients.map(
                                  (ingredient, ingredientIndex) => {
                                    const ingredientUpdater =
                                      createPathUpdater<StepIngredientModification>(
                                        updateEditedDish,
                                        `steps[${stepIndex}].ingredients[${ingredientIndex}]`,
                                      );
                                    const ingFood = getFoodMaybe(
                                      ingredient.foodUnitId,
                                      foods,
                                    );
                                    return (
                                      <IngredientEditor
                                        key={getRenderKey(
                                          ingredient,
                                          ingredientIndex,
                                        )}
                                        ingredient={ingredient}
                                        updateIngredient={ingredientUpdater}
                                        foods={foods}
                                        purchases={purchases}
                                        personLookup={personLookup}
                                        loadFood={loadFood}
                                        fromCookbook={!!editedDish.fromCookbook}
                                        purchaseRelevant={
                                          !!editedDish.purchaseRelevant
                                        }
                                        hasNonDefaultPurchase={
                                          ingFood != null &&
                                          foodsWithNonDefaultPurchase.has(
                                            ingFood.id,
                                          )
                                        }
                                        editAvailabilities={(purchaseId) =>
                                          availabilityDrawerProps.editAvailabilities(
                                            purchaseId,
                                            availabilities.filter(
                                              (a) =>
                                                a.purchaseId === purchaseId,
                                            ),
                                          )
                                        }
                                        deleteIngredient={() => {
                                          updateStep((s) => {
                                            s.ingredients.splice(
                                              ingredientIndex,
                                              1,
                                            );
                                          });
                                        }}
                                      />
                                    );
                                  },
                                )}
                              </div>
                              <AddDashedButton
                                className="mt-2"
                                dataCy={`dish-step-add-ingredient-button-${stepIndex}`}
                                onClick={() => openAddIngredient(stepIndex)}
                              >
                                <Glyph icon="plus" size={15} />
                                <span>Zutat hinzufügen</span>
                              </AddDashedButton>
                              {hasNonDefaultPurchaseInStep && (
                                <Hint className="flex items-center gap-2 rounded-card border border-line bg-surface p-2">
                                  <Glyph icon="purchase" size={24} />
                                  <span className={"text-xs"}>
                                    Kochdialog fragt hier nach dem konkreten
                                    Produkt.
                                  </span>
                                </Hint>
                              )}
                              <div className="flex justify-center">
                                <SmallIconButton
                                  label="Schritt löschen"
                                  icon="delete"
                                  variant="destructive"
                                  onClick={() =>
                                    updateEditedDish((d) => {
                                      d.steps.splice(stepIndex, 1);
                                    })
                                  }
                                />
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        )}
                      </SortableStep>
                    );
                  })}
                </Accordion>
              </SortableContext>
            </DndContext>
            <AddDashedButton
              className="mt-2 font-medium"
              dataCy="dish-add-step-button"
              onClick={addStep}
            >
              <Glyph icon="plus" size={15} />
              <span>Schritt hinzufügen</span>
            </AddDashedButton>
          </Card>
        )}

        {/* Nährwerte – eine Card */}
        <Card className="p-3">
          <SectionLabel>Nährwerte</SectionLabel>
          <div className="grid grid-cols-2 gap-3 text-center">
            <div>
              <p className="text-xs text-ink-muted">Gesamt</p>
              <p className="text-lg font-bold text-ink">
                {formatKcal(dishNutri.kcal)}
              </p>
              <p className="text-xs text-ink-muted">kcal</p>
            </div>
            <div>
              <p className="text-xs text-ink-muted">Pro Portion</p>
              <p className="text-lg font-bold text-ink-muted">
                {formatKcal(perPortion.kcal)}
              </p>
              <p className="text-xs text-ink-muted">kcal</p>
            </div>
          </div>
          <MacroBar
            fat={dishNutri.fat}
            carbs={dishNutri.carbs}
            protein={dishNutri.protein}
            kcal={dishNutri.kcal}
            showExtendedInfo={false}
            showLabels={false}
            className="mt-3 h-3"
          />
          <div className="mt-2 grid grid-cols-3 gap-1 text-center text-xs">
            <div>
              <span className="font-semibold text-gold">F </span>
              <span className="text-ink">{formatGrams(dishNutri.fat)}</span>
            </div>
            <div>
              <span className="font-semibold text-teal">KH </span>
              <span className="text-ink">{formatGrams(dishNutri.carbs)}</span>
            </div>
            <div>
              <span className="font-semibold text-protein">P </span>
              <span className="text-ink">{formatGrams(dishNutri.protein)}</span>
            </div>
            <div>
              <span className="text-ink-muted">
                {" "}
                ({formatGrams(perPortion.fat)}/P)
              </span>
            </div>
            <div>
              <span className="text-ink-muted">
                {" "}
                ({formatGrams(perPortion.carbs)}/P)
              </span>
            </div>
            <div>
              <span className="text-ink-muted">
                {" "}
                ({formatGrams(perPortion.protein)}/P)
              </span>
            </div>
          </div>
        </Card>

        {/* Zutaten-Aufschlüsselung */}
        {ingredientBreakdown.length > 0 && (
          <Accordion>
            <AccordionItem
              value="breakdown"
              className="rounded-card border border-line bg-surface-card shadow-card"
            >
              <AccordionTrigger className="px-3 py-3 hover:no-underline">
                <div className="flex justify-between w-full">
                  <span className="text-sm font-semibold text-ink-muted">
                    Zutaten-Aufschlüsselung
                  </span>
                  <div className="rounded-card border border-line flex justify-center items-center p-2">
                    <Glyph icon="atListReceipt" />
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="p-0">
                <ul className="divide-y divide-line border-t border-line">
                  {ingredientBreakdown.map((row, i) => (
                    <li key={i} className="px-3 py-2">
                      <div className="flex items-center justify-between gap-3 text-sm">
                        <span className="truncate text-ink">{row.label}</span>
                      </div>
                      <MacroBar
                        className="mt-1"
                        fat={row.nutri.fat}
                        carbs={row.nutri.carbs}
                        protein={row.nutri.protein}
                        kcal={row.nutri.kcal}
                        showExtendedInfo={false}
                      />
                    </li>
                  ))}
                  <li className="flex items-center justify-between gap-3 bg-surface px-3 py-2 text-sm font-semibold rounded-b-card">
                    <span className="text-ink">Gesamt</span>
                    <span className="text-gold">
                      {formatKcal(dishNutri.kcal)} kcal
                    </span>
                  </li>
                </ul>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        )}

        {/* Nährwerte je Person */}
        {activePersons.length > 0 && (
          <div className="flex flex-col gap-2">
            {activePersons.map((person) => {
              const pNutri = computePersonNutri(person.id);
              const pBreakdown = computePersonIngredientBreakdown(person.id);
              return (
                <Accordion key={person.id}>
                  <AccordionItem
                    value="person-nutri"
                    className="rounded-card border border-line bg-surface-card shadow-card"
                  >
                    <AccordionTrigger className="px-3 py-3 hover:no-underline">
                      <div className="mr-2 flex-1">
                        <PersonNutriSummary name={person.name} nutri={pNutri} />
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="p-0">
                      {pBreakdown.length > 0 && (
                        <ul className="divide-y divide-line border-t border-line">
                          {pBreakdown.map((row, i) => (
                            <li key={i} className="px-3 py-2 bg-surface">
                              <div className="flex items-center justify-between gap-3 text-sm">
                                <span className="truncate text-ink">
                                  {row.label}
                                </span>
                              </div>
                              <MacroBar
                                className="mt-1"
                                fat={row.nutri.fat}
                                carbs={row.nutri.carbs}
                                protein={row.nutri.protein}
                                kcal={row.nutri.kcal}
                                showExtendedInfo={false}
                              />
                            </li>
                          ))}
                        </ul>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              );
            })}
          </div>
        )}

        {!hideSaveButton && (
          <div className="flex items-center justify-center pt-2">
            <SaveButton isDisabled={pristine && !isCreate} save={saveDish} />
          </div>
        )}
      </div>
    </>
  );
};

export type DishEditorProps = {
  isCreate: boolean;
  personLookup: PersonLookup;
  initialDish: DishModification;
  loadAvailabilities: () => Promise<void>;
  afterSave?: (createdDish: any, isFood: boolean) => void;
  /** "simple" blendet Basics/Portionen/Schritte aus und erzwingt die Zutaten-Karte. */
  variant?: "full" | "simple";
  /** Blendet das Bezeichnungsfeld aus (z. B. wenn der Name außerhalb gepflegt wird). */
  hideDescription?: boolean;
  /** Blendet den Speichern-Button aus (z. B. wenn außerhalb gespeichert wird). */
  hideSaveButton?: boolean;
  /** Meldet den aktuellen Gerichtsentwurf inkl. geladener Foods nach außen. */
  onDishChange?: (dish: DishModification, foods: FoodDto[]) => void;
  /** Ersetzt das interne Speichern (z. B. Kaufprodukt aus Gericht anlegen). */
  saveOverride?: (dish: DishModification) => Promise<boolean>;
};
